/**
 * Modal de Confirmação de Upload
 * Permite ao usuário configurar opções de análise antes de enviar os arquivos
 */

import React, { useState, useMemo, useEffect } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  keyframes,
  Collapse,
  useMediaQuery,
  useTheme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import EmailIcon from '@mui/icons-material/Email'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied'
import LabelIcon from '@mui/icons-material/Label'
import TopicIcon from '@mui/icons-material/Topic'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import SummarizeIcon from '@mui/icons-material/Summarize'
import TimelineIcon from '@mui/icons-material/Timeline'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SecurityIcon from '@mui/icons-material/Security'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LockIcon from '@mui/icons-material/Lock'
import {
  UploadOptions,
  AnalysisType,
  ANALYSIS_TYPES,
  DEFAULT_UPLOAD_OPTIONS
} from '../../../types/uploadOptions'
import { BETA_LIMITS } from '../../../utils/betaLimits'
import { getUploadQuota, UploadQuotaResponse, getUserUploadPreferences, saveUserUploadPreferences } from '../../../services/api'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

// Mapeamento de ícones
const ANALYSIS_ICONS: Record<string, React.ReactNode> = {
  SentimentSatisfied: <SentimentSatisfiedIcon />,
  Label: <LabelIcon />,
  Topic: <TopicIcon />,
  RecordVoiceOver: <RecordVoiceOverIcon />,
  Summarize: <SummarizeIcon />,
  Timeline: <TimelineIcon />
}

interface Props {
  open: boolean
  files: File[]
  onClose: () => void
  onConfirm: (files: File[], options: UploadOptions) => void
  onRemoveFile?: (index: number) => void
  userEmail?: string
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// ============================================
// Configurações de Capacidade de Processamento
// ============================================
// Produção: 3 instâncias × 5 arquivos = 15 arquivos simultâneos
const PARALLEL_TRANSCRIPTION_CAPACITY = 15 // Arquivos transcritos em paralelo (assíncrono)
const PARALLEL_ANALYSIS_CAPACITY = 15      // Arquivos analisados em paralelo (3 inst × 5 arq)
const ANALYSIS_THREADS_PER_FILE = 8        // Análises paralelas por arquivo

// Calcula tempo estimado de transcrição baseado no tamanho dos arquivos
// Considera processamento paralelo no backend
function estimateTranscriptionTime(bytes: number, fileCount: number = 1): number {
  // Estimativa: ~1MB = ~1 minuto de áudio, transcrição leva ~30s por minuto
  const estimatedMinutes = bytes / (1024 * 1024)
  const totalSeconds = Math.ceil(estimatedMinutes * 30)
  
  // Se for chamado para um único arquivo, retorna tempo individual
  if (fileCount <= 1) return totalSeconds
  
  // Com processamento paralelo (15 arquivos simultâneos)
  // Divide em lotes baseado na capacidade
  const batches = Math.ceil(fileCount / PARALLEL_TRANSCRIPTION_CAPACITY)
  
  // Tempo médio por arquivo no lote (o maior arquivo determina o tempo do lote)
  const avgTimePerFile = totalSeconds / fileCount
  const batchTime = avgTimePerFile * Math.min(fileCount, PARALLEL_TRANSCRIPTION_CAPACITY)
  
  return Math.ceil(batchTime * batches / PARALLEL_TRANSCRIPTION_CAPACITY)
}

// Calcula tempo de transcrição para múltiplos arquivos considerando paralelismo
function estimateTotalTranscriptionTime(files: File[]): number {
  if (files.length === 0) return 0
  
  // Ordenar arquivos por tamanho (maior primeiro) para estimar melhor
  const sortedSizes = files.map(f => f.size).sort((a, b) => b - a)
  
  // Com 15 arquivos paralelos, processa em lotes
  const batches = Math.ceil(files.length / PARALLEL_TRANSCRIPTION_CAPACITY)
  let totalTime = 0
  
  for (let i = 0; i < batches; i++) {
    const batchStart = i * PARALLEL_TRANSCRIPTION_CAPACITY
    const batchEnd = Math.min(batchStart + PARALLEL_TRANSCRIPTION_CAPACITY, files.length)
    // O tempo do lote é determinado pelo maior arquivo do lote
    const largestInBatch = sortedSizes[batchStart]
    totalTime += estimateTranscriptionTime(largestInBatch)
  }
  
  return totalTime
}

// Formata segundos para exibição amigável
function formatTime(seconds: number): string {
  if (seconds < 60) return `~${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (secs === 0) return `~${mins}min`
  return `~${mins}min ${secs}s`
}

export default function UploadConfirmationModal({
  open,
  files,
  onClose,
  onConfirm,
  onRemoveFile,
  userEmail
}: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const emailKnown = !!userEmail
  const [options, setOptions] = useState<UploadOptions>({
    ...DEFAULT_UPLOAD_OPTIONS,
    notifyByEmail: emailKnown,
    notificationEmail: userEmail
  })
  const [showAdvanced, setShowAdvanced] = useState(emailKnown)

  // Estado de cota diária de uploads — carregado do backend quando o modal abre
  const [quota, setQuota] = useState<UploadQuotaResponse | null>(null)
  const [quotaLoading, setQuotaLoading] = useState(false)

  // Reset options when modal opens or userEmail changes
  useEffect(() => {
    if (open) {
      const knownEmail = !!userEmail

      // Carrega preferências do backend e aplica como defaults.
      // Se o usuário nunca salvou, usa o fallback (notifyByEmail = knownEmail).
      getUserUploadPreferences()
        .then(prefs => {
          const hasNotify = prefs.notifyByEmail != null
          setOptions({
            ...DEFAULT_UPLOAD_OPTIONS,
            notifyByEmail: hasNotify ? !!prefs.notifyByEmail : knownEmail,
            notificationEmail: prefs.notificationEmail ?? userEmail,
            retainMedia: prefs.retainMedia ?? false
          })
          setShowAdvanced(hasNotify ? !!prefs.notifyByEmail : knownEmail)
        })
        .catch(() => {
          // Fallback caso o endpoint falhe
          setOptions({
            ...DEFAULT_UPLOAD_OPTIONS,
            notifyByEmail: knownEmail,
            notificationEmail: userEmail
          })
          setShowAdvanced(knownEmail)
        })

      // Busca cota diária do backend ao abrir o modal.
      // A flag betaModeEnabled vem na resposta — o frontend não mantém essa config localmente.
      setQuotaLoading(true)
      getUploadQuota()
        .then(q => setQuota(q))
        .catch(() => setQuota(null))
        .finally(() => setQuotaLoading(false))
    }
  }, [open, userEmail])

  // Calcular totais
  const totalSize = useMemo(() => 
    files.reduce((sum, f) => sum + f.size, 0), 
    [files]
  )

  // Verifica se a cota diária comporta a quantidade de arquivos selecionados.
  // Todos os valores — incluindo betaModeEnabled — vêm do backend via quota response.
  const betaModeEnabled = quota?.betaModeEnabled ?? false
  const quotaRemaining = quota?.remaining ?? Infinity
  const quotaLimit = quota?.limit ?? BETA_LIMITS.dailyUploadQuota
  const quotaUsed = quota?.used ?? 0
  const quotaExceeded = betaModeEnabled && quota !== null && quotaRemaining < files.length

  // Verifica arquivos individuais que excedem o limite de tamanho no modo BETA
  const maxFileBytes = betaModeEnabled ? (quota?.maxFileBytes ?? null) : null
  const oversizedFiles = maxFileBytes !== null
    ? new Set(files.filter(f => f.size > maxFileBytes))
    : new Set<File>()
  const hasSizeViolation = oversizedFiles.size > 0

  // Verifica se a quantidade de arquivos excede o limite de envio simultâneo no modo BETA
  const countExceeded = betaModeEnabled && files.length > BETA_LIMITS.maxConcurrentUploads

  // Calcular tempo estimado de transcrição (considerando processamento paralelo)
  const estimatedTranscriptionSeconds = useMemo(() => 
    estimateTotalTranscriptionTime(files),
    [files]
  )

  // Calcular tempo estimado de análise (baseado nas análises selecionadas)
  // Com processamento paralelo: 15 arquivos simultâneos, 8 análises paralelas por arquivo
  const estimatedAnalysisSeconds = useMemo(() => {
    if (!options.enableAnalysis) return 0
    
    // Somar tempos estimados das análises selecionadas
    const timePerFileSequential = options.selectedAnalyses.reduce((sum, analysisId) => {
      const analysis = ANALYSIS_TYPES.find(t => t.id === analysisId)
      if (analysis) {
        // Extrair número do estimatedTime (ex: "~30s" -> 30)
        const match = analysis.estimatedTime.match(/~?(\d+)/)
        return sum + (match ? parseInt(match[1], 10) : 30)
      }
      return sum + 30
    }, 0)
    
    // Com 8 threads paralelas por arquivo, dividimos o tempo
    const timePerFileParallel = Math.ceil(timePerFileSequential / ANALYSIS_THREADS_PER_FILE)
    
    // Com 15 arquivos em paralelo, calculamos lotes
    const batches = Math.ceil(files.length / PARALLEL_ANALYSIS_CAPACITY)
    
    return timePerFileParallel * batches
  }, [options.enableAnalysis, options.selectedAnalyses, files.length])

  const selectedCount = options.selectedAnalyses.length

  // Handlers
  const handleToggleAnalysis = (analysisId: AnalysisType) => {
    setOptions(prev => ({
      ...prev,
      selectedAnalyses: prev.selectedAnalyses.includes(analysisId)
        ? prev.selectedAnalyses.filter(id => id !== analysisId)
        : [...prev.selectedAnalyses, analysisId]
    }))
  }

  const handleSelectAll = () => {
    setOptions(prev => ({
      ...prev,
      selectedAnalyses: ANALYSIS_TYPES.map(t => t.id)
    }))
  }

  const handleDeselectAll = () => {
    setOptions(prev => ({
      ...prev,
      selectedAnalyses: []
    }))
  }

  const handleConfirm = () => {
    // Se análise está desabilitada, limpar seleções
    const finalOptions: UploadOptions = {
      ...options,
      selectedAnalyses: options.enableAnalysis ? options.selectedAnalyses : []
    }

    // Persiste as preferências do usuário (fire-and-forget)
    saveUserUploadPreferences({
      notifyByEmail: finalOptions.notifyByEmail,
      notificationEmail: finalOptions.notificationEmail,
      retainMedia: finalOptions.retainMedia
    }).catch(() => { /* silencioso — não bloqueia o upload */ })

    onConfirm(files, finalOptions)
  }

  const isVideo = (file: File) => file.type.startsWith('video/')

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          animation: `${fadeIn} 0.2s ease-out`
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
            <Box
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#6366f1', 0.1),
                color: 'primary.main'
              }}
            >
              <CloudUploadIcon sx={{ fontSize: { xs: 22, sm: 28 } }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Confirmar Upload
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {files.length} arquivo{files.length > 1 ? 's' : ''} • {formatBytes(totalSize)}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Aviso de limites BETA — exibido apenas quando betaModeEnabled=true vindo do backend */}
        {betaModeEnabled && (
          <Alert
            severity={quotaExceeded || hasSizeViolation || countExceeded ? 'error' : 'warning'}
            sx={{
              borderRadius: 0,
              py: 0.75,
              px: { xs: 2, sm: 3 },
              alignItems: 'center',
              '& .MuiAlert-icon': { py: 0, alignItems: 'center' },
              '& .MuiAlert-message': { py: 0, width: '100%' }
            }}
          >
            <AlertTitle sx={{ fontSize: '0.7rem', fontWeight: 700, mb: 0.25, mt: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Versão de avaliação
            </AlertTitle>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0.25, sm: 3 }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Typography variant="caption" fontWeight={500}>
                Limite por arquivo: <strong>{Math.round((quota?.maxFileBytes ?? 209715200) / (1024 * 1024))} MB</strong>
              </Typography>
              <Typography variant="caption" fontWeight={500}>
                Máximo por envio: <strong>{BETA_LIMITS.maxConcurrentUploads} arquivo{BETA_LIMITS.maxConcurrentUploads !== 1 ? 's' : ''}</strong>
              </Typography>
              {countExceeded ? (
                <Typography variant="caption" fontWeight={600} color="error.main">
                  Limite excedido — remova {files.length - BETA_LIMITS.maxConcurrentUploads} arquivo{files.length - BETA_LIMITS.maxConcurrentUploads !== 1 ? 's' : ''} para continuar
                </Typography>
              ) : hasSizeViolation ? (
                <Typography variant="caption" fontWeight={600} color="error.main">
                  {oversizedFiles.size} arquivo{oversizedFiles.size > 1 ? 's' : ''} acima do limite — remova-{oversizedFiles.size > 1 ? 'os' : 'o'} para continuar
                </Typography>
              ) : quotaExceeded ? (
                <Typography variant="caption" fontWeight={600} color="error.main">
                  Cota diária esgotada — {quotaRemaining} de {quotaLimit} disponíveis
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  fontWeight={500}
                  color={quotaRemaining <= 1 ? 'error.main' : 'inherit'}
                >
                  Uploads restantes hoje:{' '}
                  <strong>
                    {quotaLoading ? '...' : `${quotaRemaining} de ${quotaLimit}`}
                  </strong>
                </Typography>
              )}
            </Stack>
          </Alert>
        )}

        {/* Lista de arquivos */}
        <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha('#f5f5f5', 0.5) }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={{ xs: 0.5, sm: 0 }}
            mb={1}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Arquivos selecionados ({files.length})
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AccessTimeIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                Transcrição: <strong>{formatTime(estimatedTranscriptionSeconds)}</strong>
                {options.enableAnalysis && estimatedAnalysisSeconds > 0 && (
                  <> + Análise: <strong>{formatTime(estimatedAnalysisSeconds)}</strong></>
                )}
              </Typography>
            </Stack>
          </Stack>
          <Stack spacing={1} sx={{ maxHeight: { xs: 150, sm: 180 }, overflow: 'auto' }}>
            {files.map((file, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  p: { xs: 1, sm: 1.5 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.5 },
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  borderColor: oversizedFiles.has(file) ? 'error.main' : undefined,
                  bgcolor: oversizedFiles.has(file) ? alpha('#ef4444', 0.04) : undefined,
                  '&:hover': {
                    bgcolor: oversizedFiles.has(file) ? alpha('#ef4444', 0.08) : alpha('#f5f5f5', 0.8),
                    '& .remove-btn': { opacity: 1 }
                  }
                }}
              >
                {isVideo(file) ? (
                  <VideoFileIcon sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 24 } }} />
                ) : (
                  <AudioFileIcon sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 24 } }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography
                      variant="caption"
                      color={oversizedFiles.has(file) ? 'error.main' : 'text.secondary'}
                      fontWeight={oversizedFiles.has(file) ? 600 : 400}
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    >
                      {formatBytes(file.size)}
                      {oversizedFiles.has(file) && maxFileBytes !== null && (
                        <> — excede {Math.round(maxFileBytes / (1024 * 1024))} MB</>
                      )}
                    </Typography>
                    {!oversizedFiles.has(file) && (
                      <>
                        <Typography variant="caption" color="text.disabled">•</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          {formatTime(estimateTranscriptionTime(file.size))}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Box>
                {onRemoveFile && files.length > 1 && (
                  <Tooltip title="Remover arquivo">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveFile(index)}
                      className="remove-btn"
                      sx={{
                        opacity: isMobile ? 0.7 : 0.5,
                        transition: 'opacity 0.2s',
                        '&:hover': { color: 'error.main' }
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {oversizedFiles.has(file)
                  ? <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: { xs: 18, sm: 20 } }} />
                  : <CheckCircleIcon sx={{ color: 'success.main', fontSize: { xs: 18, sm: 20 } }} />
                }
              </Paper>
            ))}
          </Stack>
        </Box>

        <Divider />

        {/* Opções de análise */}
        <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1, overflow: 'auto' }}>
          {/* Toggle principal - Análise Inteligente */}
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              mb: { xs: 2, sm: 3 },
              borderRadius: 2,
              bgcolor: options.enableAnalysis ? alpha('#6366f1', 0.04) : 'transparent',
              borderColor: options.enableAnalysis ? 'primary.main' : 'divider',
              transition: 'all 0.2s'
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={{ xs: 1, sm: 2 }}>
              <Stack direction="row" alignItems="flex-start" spacing={{ xs: 1.5, sm: 2 }}>
                <Box
                  sx={{
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: options.enableAnalysis ? alpha('#6366f1', 0.15) : alpha('#9ca3af', 0.1),
                    color: options.enableAnalysis ? 'primary.main' : 'text.secondary',
                    transition: 'all 0.2s',
                    flexShrink: 0
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} mb={0.5}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      {isMobile ? 'Análise com IA' : 'Análise Inteligente com IA'}
                    </Typography>
                    {options.enableAnalysis && (
                      <Chip
                        label="Recomendado"
                        size="small"
                        color="primary"
                        sx={{ fontSize: '0.6rem', height: 18 }}
                      />
                    )}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      lineHeight: 1.5,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', sm: 'block' }
                    }}
                  >
                    Após a transcrição, nossa inteligência artificial analisa o conteúdo de forma
                    automática, extraindo insights valiosos como sentimentos, entidades, tópicos
                    principais e muito mais.
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      lineHeight: 1.4,
                      fontSize: '0.75rem',
                      display: { xs: 'block', sm: 'none' }
                    }}
                  >
                    Extrai sentimentos, entidades e tópicos automaticamente.
                  </Typography>

                  {/* Badges de segurança */}
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    <Chip
                      icon={<LockIcon sx={{ fontSize: '12px !important' }} />}
                      label={isMobile ? 'Criptografado' : 'Dados criptografados'}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                        height: { xs: 20, sm: 24 },
                        borderColor: alpha('#10b981', 0.5),
                        color: '#10b981',
                        '& .MuiChip-icon': { color: '#10b981' }
                      }}
                    />
                    <Chip
                      icon={<SecurityIcon sx={{ fontSize: '12px !important' }} />}
                      label={isMobile ? 'Seguro' : 'Processamento seguro'}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                        height: { xs: 20, sm: 24 },
                        borderColor: alpha('#10b981', 0.5),
                        color: '#10b981',
                        '& .MuiChip-icon': { color: '#10b981' }
                      }}
                    />
                    {!isMobile && (
                      <Tooltip title="Seus dados são processados em ambiente isolado e não são compartilhados ou usados para treinamento de IA. Após o processamento, os arquivos originais podem ser removidos sob sua solicitação.">
                        <Chip
                          icon={<InfoOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                          label="Saiba mais"
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            height: 24,
                            cursor: 'help',
                            '&:hover': { bgcolor: alpha('#6366f1', 0.05) }
                          }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              </Stack>
              <Switch
                checked={options.enableAnalysis}
                onChange={(e) => setOptions(prev => ({
                  ...prev,
                  enableAnalysis: e.target.checked
                }))}
                color="primary"
                sx={{ mt: 0, flexShrink: 0 }}
              />
            </Stack>
            
            {/* Tempo estimado de análise */}
            {options.enableAnalysis && estimatedAnalysisSeconds > 0 && (
              <Box
                sx={{
                  mt: { xs: 1.5, sm: 2 },
                  pt: { xs: 1.5, sm: 2 },
                  borderTop: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  {isMobile
                    ? `+${formatTime(estimatedAnalysisSeconds)} para análise`
                    : `Tempo adicional para análise de ${files.length} arquivo${files.length > 1 ? 's' : ''}`}
                </Typography>
                {!isMobile && (
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                    label={formatTime(estimatedAnalysisSeconds)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            )}
          </Paper>

          {/* Seleção de análises */}
          <Collapse in={options.enableAnalysis}>
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={1}
                mb={2}
              >
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Tipos de análise ({selectedCount}/{ANALYSIS_TYPES.length})
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={handleSelectAll} sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>
                    {isMobile ? 'Todos' : 'Selecionar todos'}
                  </Button>
                  <Button size="small" onClick={handleDeselectAll} color="inherit" sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>
                    Limpar
                  </Button>
                </Stack>
              </Stack>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: { xs: 1, sm: 1.5 }
              }}>
                {ANALYSIS_TYPES.map((analysis) => {
                  const isSelected = options.selectedAnalyses.includes(analysis.id)
                  return (
                    <Paper
                      key={analysis.id}
                      variant="outlined"
                      onClick={() => handleToggleAnalysis(analysis.id)}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        bgcolor: isSelected ? alpha('#6366f1', 0.03) : 'transparent',
                        '&:hover': {
                          borderColor: 'primary.light',
                          bgcolor: alpha('#6366f1', 0.05)
                        }
                      }}
                    >
                      <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="flex-start">
                        <Checkbox
                          checked={isSelected}
                          size="small"
                          sx={{ mt: -0.5, ml: -0.5, p: { xs: 0.5, sm: 1 } }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                            <Box sx={{ color: isSelected ? 'primary.main' : 'text.secondary', display: 'flex' }}>
                              {ANALYSIS_ICONS[analysis.icon]}
                            </Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                              {analysis.label}
                            </Typography>
                            {!isMobile && (
                              <Tooltip title={analysis.description} arrow placement="top">
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
                              </Tooltip>
                            )}
                          </Stack>
                          {!isMobile && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {analysis.description.substring(0, 60)}...
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={analysis.estimatedTime}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, height: { xs: 20, sm: 24 }, flexShrink: 0 }}
                        />
                      </Stack>
                    </Paper>
                  )
                })}
              </Box>
            </Box>
          </Collapse>

          <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

          {/* Opções avançadas */}
          <Box>
            <Button
              onClick={() => setShowAdvanced(!showAdvanced)}
              endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              {isMobile ? 'Avançado' : 'Opções avançadas'}
            </Button>

            <Collapse in={showAdvanced}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  bgcolor: options.notifyByEmail ? alpha('#10b981', 0.03) : 'transparent',
                  borderColor: options.notifyByEmail ? 'success.main' : 'divider',
                  transition: 'all 0.2s'
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={{ xs: 1.5, sm: 2 }}>
                  <EmailIcon
                    color={options.notifyByEmail ? 'success' : 'action'}
                    sx={{ mt: 0.5, fontSize: { xs: 20, sm: 24 } }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          Notificar por Email
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}
                        >
                          Receba um aviso quando o processamento terminar
                        </Typography>
                      </Box>
                      <Switch
                        checked={options.notifyByEmail}
                        onChange={(e) => setOptions(prev => ({
                          ...prev,
                          notifyByEmail: e.target.checked
                        }))}
                        color="success"
                      />
                    </Stack>

                    <Collapse in={options.notifyByEmail}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Email para notificação"
                        placeholder={userEmail || 'seu@email.com'}
                        value={options.notificationEmail || ''}
                        onChange={(e) => setOptions(prev => ({
                          ...prev,
                          notificationEmail: e.target.value
                        }))}
                        sx={{ mt: { xs: 1.5, sm: 2 } }}
                        helperText={isMobile ? '' : 'Deixe em branco para usar o email da sua conta'}
                      />
                    </Collapse>
                  </Box>
                </Stack>
              </Paper>

              {/* Retenção de mídia para reprodução */}
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  mt: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  bgcolor: options.retainMedia ? alpha('#6366f1', 0.03) : 'transparent',
                  borderColor: options.retainMedia ? 'primary.main' : 'divider',
                  transition: 'all 0.2s'
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={{ xs: 1.5, sm: 2 }}>
                  <PlayCircleOutlineIcon
                    color={options.retainMedia ? 'primary' : 'action'}
                    sx={{ mt: 0.5, fontSize: { xs: 20, sm: 24 } }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          Reter mídia para reprodução
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}
                        >
                          Mantém o arquivo original por 72h para ouvir junto com a transcrição
                        </Typography>
                      </Box>
                      <Switch
                        checked={options.retainMedia}
                        onChange={(e) => setOptions(prev => ({
                          ...prev,
                          retainMedia: e.target.checked
                        }))}
                        color="primary"
                      />
                    </Stack>
                    <Collapse in={options.retainMedia}>
                      <Alert severity="info" variant="outlined" sx={{ mt: 1.5, py: 0.5 }}>
                        <Typography variant="caption">
                          A mídia será removida automaticamente após 72 horas. Apenas a transcrição permanece armazenada.
                        </Typography>
                      </Alert>
                    </Collapse>
                  </Box>
                </Stack>
              </Paper>
            </Collapse>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 2 },
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          '& > button': { width: { xs: '100%', sm: 'auto' } }
        }}
      >
        <Button onClick={onClose} color="inherit" sx={{ minHeight: { xs: 44, sm: 36 } }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={quotaExceeded || hasSizeViolation || countExceeded}
          startIcon={<CloudUploadIcon />}
          sx={{
            px: { xs: 2, sm: 3 },
            minHeight: { xs: 48, sm: 36 },
            backgroundImage: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            fontSize: { xs: '0.85rem', sm: '0.875rem' }
          }}
        >
          {isMobile
            ? (options.enableAnalysis ? `Enviar (${selectedCount} análises)` : 'Enviar')
            : (options.enableAnalysis
                ? `Enviar e Analisar (${selectedCount} análises)`
                : 'Enviar (apenas transcrição)')
          }
        </Button>
      </DialogActions>
    </Dialog>
  )
}
